import { sessionUrl } from '../config';
import type { ChatErrorCode, Message, Session } from '../types/types.ts';

export type ApiResult<T> = { data: T } | { error: ChatErrorCode };

// Bei diesen Endpunkten heißt 404 immer: Session unbekannt, gelöscht oder fremd
function toErrorCode(status: number): ChatErrorCode {
  if (status === 401) {
    return 'UNAUTHORIZED';
  }
  if (status === 404) {
    return 'SESSION_NOT_FOUND';
  }
  return 'HTTP_ERROR';
}

type ApiOptions = {
  getAccessToken: () => Promise<string | undefined>;
  signal?: AbortSignal;
};

async function request<T>(
  path: string,
  method: 'GET' | 'DELETE' | 'POST',
  { getAccessToken, signal }: ApiOptions,
  body?: unknown,
): Promise<ApiResult<T>> {
  try {
    const token = await getAccessToken();
    if (!token) {
      return { error: 'UNAUTHORIZED' };
    }
    const response = await fetch(`${sessionUrl}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
    });
    if (!response.ok) {
      return { error: toErrorCode(response.status) };
    }
    // DELETE und POST cancel antworten mit 204 ohne Body
    const data = response.status === 204 ? undefined : await response.json();
    return { data: data as T };
  } catch {
    return { error: 'NETWORK_ERROR' };
  }
}

export const fetchSessions = (options: ApiOptions) =>
  request<Session[]>('', 'GET', options);

export const fetchMessages = (sessionId: string, options: ApiOptions) =>
  request<Message[]>(
    `/${encodeURIComponent(sessionId)}/messages`,
    'GET',
    options,
  );

export const cancelAnswer = (
  sessionId: string,
  partialMessage: string,
  options: ApiOptions,
) =>
  request<undefined>(
    `/${encodeURIComponent(sessionId)}/cancel`,
    'POST',
    options,
    { partialMessage },
  );

export const deleteSession = (sessionId: string, options: ApiOptions) =>
  request<undefined>(`/${encodeURIComponent(sessionId)}`, 'DELETE', options);
